import { isValid } from '../../../../../helpers/common';
import { IServices } from '../../../../../types';
import { isEmergencyAddressComplete } from '../helpers/isEmergencyAddressComplete';
import { IValues } from '../types';
import { useGetVoWifiConfig } from './useGetVoWifiConfig';

type UseGetUseCaseSettingsProps = {
  values: IValues;
  services: IServices;
  state: string;
  isPortalPreview: boolean;
};

export const useGetUseCaseSettings = ({
  values,
  services,
  state,
  isPortalPreview,
}: UseGetUseCaseSettingsProps) => {
  const configurationSettings = values?.configuration_settings;

  // Log error when configuration settings are missing, as this is critical for the form's functionality.
  if (!configurationSettings && !isPortalPreview) {
    // Add color to the console error for better visibility
    console.log(
      '%c configuration_settings are missing for the current use case. Please check brading api on network tab for more details.',
      'color: red; font-weight: bold;',
    );
  }

  const { data, isLoading, isFetching, error } = useGetVoWifiConfig(
    state,
    services,
    true,
  );

  const requiresTc = isValid(configurationSettings?.requires_tc);
  const requiresAddress = isValid(configurationSettings?.requires_address);
  // Gates Google Places autocomplete + the required street_number field.
  // Read from GET /branding configuration_settings — NOT from the VoWiFi
  // state endpoints (UES-11807 decision).
  const autocompleteEnabled = isValid(
    configurationSettings?.emergency_address_autocomplete_enabled,
  );

  if (!requiresAddress) {
    console.warn(
      'Address form disabled. Verify branding API config if unexpected.',
    );
  } else if (!requiresTc) {
    console.warn('Address required but T&C disabled — verify branding API.');
  }

  /**
   * ⚠️ Important distinction between fields:
   *
   * - `requires_subscriber_authentication`:
   *   Used by the backend (BE) to trigger OAuth authentication redirection.
   *
   * - `requires_address`:
   *   Set by the backend to control UI behavior, specifically to show or hide
   *   sections of the form (in this case, the address form).
   *
   * 🚫 Do NOT confuse these two fields—they serve completely different purposes.
   *
   * - `requires_tc`:
   *   This one is related to VoWifiActivationPage. If it appears enabled in the form
   *   but is disabled here, it is likely due to a backend configuration.
   */
  const addressData = data?.emergency_address || null;

  return {
    requiresTc,
    requiresAddress,
    autocompleteEnabled,
    carrierCountry: configurationSettings?.country,
    addressData,
    hasPrefilledAddress: isEmergencyAddressComplete(addressData),
    isLoadingAddress: isLoading || isFetching,
    settingsError: error,
  };
};
